import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import {
  supabase,
} from "../supabaseClient";

import {
  API_BASE_URL,
} from "../config/apiBase";

import {
  getBlogAttribution,
  trackAttributedEventOnce,
} from "../utils/blogAnalytics";


// ============================================================
// AUTH CALLBACK
// ============================================================

export default function AuthCallback() {
  const navigate =
    useNavigate();


  useEffect(() => {
    // ========================================================
    // CLEANUP HELPERS
    // ========================================================

    const cleanup = () => {
      localStorage.removeItem(
        "oauth_flow"
      );

      sessionStorage.removeItem(
        "oauth_lock"
      );
    };


    const cleanupFreeToolRegistrationState =
      () => {
        localStorage.removeItem(
          "registrationSource"
        );

        localStorage.removeItem(
          "freeToolIntent"
        );
      };


    // ========================================================
    // NORMALIZE PLAN
    // ========================================================

    const normalizePlan = (
      p
    ) => {
      const v =
        (p || "")
          .toString()
          .trim()
          .toLowerCase();

      if (!v) {
        return "";
      }

      if (
        v === "free_calculator" ||
        v.includes("calculator") ||
        v === "free"
      ) {
        return "free_calculator";
      }

      if (
        v.includes("deep")
      ) {
        return "deep_audit";
      }

      if (
        v.includes("snap")
      ) {
        return "snapshot";
      }

      return "";
    };


    // ========================================================
    // NORMALIZE SOURCE
    // ========================================================

    const normalizeSource = (
      source
    ) => {
      const v =
        (source || "")
          .toString()
          .trim()
          .toLowerCase();

      if (
        v === "financial_calculator" ||
        v === "ai_risk_calculator" ||
        v === "blog_comment" ||
        v === "career_audit" ||
        v === "blog" ||
        v === "blog_article"
      ) {
        return v === "blog_article"
          ? "blog"
          : v;
      }

      return "";
    };


    // ========================================================
    // SAFE URL DECODER
    // ========================================================

    const safeDecode = (
      value
    ) => {
      if (!value) {
        return "";
      }

      try {
        return decodeURIComponent(
          value
        );
      }
      catch {
        return value;
      }
    };


    // ========================================================
    // PHASE 6C - BLOG SIGNUP TRACKING
    // ========================================================

    const trackBlogSignupIfNeeded = (
      authUid,
      finalPlan
    ) => {
      try {
        const blogAttribution =
          getBlogAttribution();

        if (
          !blogAttribution?.blogSlug ||
          !authUid
        ) {
          return;
        }

        trackAttributedEventOnce(
          `signup_from_blog_${authUid}`,
          "signup_from_blog",
          {
            ...blogAttribution,

            reportType:
              finalPlan === "deep_audit"
                ? "deep_audit"
                : finalPlan === "snapshot"
                ? "snapshot"
                : "",
          }
        );

        console.log(
          "[Phase6C] signup_from_blog tracked from AuthCallback",
          {
            authUid,
            blogSlug:
              blogAttribution.blogSlug,
            reportType:
              finalPlan,
          }
        );
      }
      catch (error) {
        console.warn(
          "[Phase6C] signup_from_blog tracking failed:",
          error
        );
      }
    };


    // ========================================================
    // MAIN AUTH FLOW
    // ========================================================

    const run =
      async () => {
        const url =
          window.location.href;

        const qs =
          new URLSearchParams(
            window.location.search
          );


        const commentReturn =
          localStorage.getItem(
            "comment_register_return"
          );


        const type =
          (
            qs.get("type") ||
            ""
          ).toLowerCase();


        const flowFromUrl =
          (
            qs.get("flow") ||
            ""
          ).toLowerCase();


        const flowFromStorage =
          (
            localStorage.getItem(
              "oauth_flow"
            ) ||
            ""
          ).toLowerCase();


        const flow =
          flowFromUrl ||
          flowFromStorage ||
          (
            type === "signup"
              ? "register"
              : "login"
          );


        // ====================================================
        // OAUTH PROVIDER ERROR
        // ====================================================

        if (
          qs.get("error")
        ) {
          cleanup();

          navigate(
            "/login?err=oauth_error",
            {
              replace:
                true,
            }
          );

          return;
        }


        // ====================================================
        // PKCE OAUTH CODE FLOW
        // ====================================================

        if (
          qs.has("code")
        ) {
          const {
            error,
          } =
            await supabase.auth
              .exchangeCodeForSession(
                url
              );

          if (error) {
            cleanup();

            navigate(
              "/login?err=exchange_failed",
              {
                replace:
                  true,
              }
            );

            return;
          }
        }


        // ====================================================
        // HASH TOKEN FLOW
        // ====================================================

        if (
          window.location.hash &&
          window.location.hash.includes(
            "access_token"
          )
        ) {
          const hashParams =
            new URLSearchParams(
              window.location.hash.substring(
                1
              )
            );


          const access_token =
            hashParams.get(
              "access_token"
            );


          const refresh_token =
            hashParams.get(
              "refresh_token"
            );


          if (
            access_token &&
            refresh_token
          ) {
            const {
              error,
            } =
              await supabase.auth
                .setSession({
                  access_token,
                  refresh_token,
                });

            if (error) {
              cleanup();

              navigate(
                "/login?err=confirm_failed",
                {
                  replace:
                    true,
                }
              );

              return;
            }
          }
          else {
            cleanup();

            navigate(
              "/login?err=confirm_failed",
              {
                replace:
                  true,
              }
            );

            return;
          }
        }


        // ====================================================
        // GET ACTIVE SUPABASE SESSION
        // ====================================================

        const {
          data,
        } =
          await supabase.auth
            .getSession();


        const session =
          data?.session;


        if (
          !session?.user
        ) {
          cleanup();

          navigate(
            "/login?err=no_session",
            {
              replace:
                true,
            }
          );

          return;
        }


        const authUid =
          session.user.id;


        const email =
          (
            session.user.email ||
            ""
          ).toLowerCase();


        const name =
          session.user.user_metadata
            ?.full_name ||
          session.user.user_metadata
            ?.name ||
          "New User";


        // ====================================================
        // REGISTER FLOW
        // ====================================================

        if (
          flow === "register"
        ) {
          let selectedPlan =
            normalizePlan(
              qs.get("plan") ||
              localStorage.getItem(
                "oauth_register_plan"
              ) ||
              localStorage.getItem(
                "selectedPlan"
              )
            );


          if (
            !selectedPlan
          ) {
            selectedPlan =
              "snapshot";
          }


          // --------------------------------------------------
          // Preserve source from LandingPage
          // --------------------------------------------------

          const sourceFromUrl =
            normalizeSource(
              qs.get("source")
            );


          const sourceFromStorage =
            normalizeSource(
              localStorage.getItem(
                "registrationSource"
              )
            );


          const freeToolIntent =
            normalizeSource(
              localStorage.getItem(
                "freeToolIntent"
              )
            );


          const blogAttribution =
            getBlogAttribution();


          let registrationSource =
            "career_audit";


          if (
            blogAttribution?.blogSlug
          ) {
            registrationSource =
              "blog";
          }


          if (
            commentReturn
          ) {
            registrationSource =
              "blog_comment";
          }
          else if (
            selectedPlan ===
            "free_calculator"
          ) {
            registrationSource =
              sourceFromUrl ||
              sourceFromStorage ||
              freeToolIntent ||
              "financial_calculator";
          }


          // --------------------------------------------------
          // Persist plan locally
          // --------------------------------------------------

          localStorage.setItem(
            "selectedPlan",
            selectedPlan
          );


          sessionStorage.setItem(
            "user_plan",
            selectedPlan
          );


          localStorage.setItem(
            `user_plan_${authUid}`,
            selectedPlan
          );


          localStorage.setItem(
            "registrationSource",
            registrationSource
          );


          try {
            // ================================================
            // IMPORTANT:
            // Uses centralized API_BASE_URL.
            //
            // LOCAL:
            // http://localhost:8080
            //
            // PRODUCTION:
            // https://api.reignitespirit.com
            // ================================================

            const registerUrl =
              `${API_BASE_URL}/api/users/register-oauth-plan`;


            console.log(
              "[AuthCallback] OAuth registration API:",
              registerUrl
            );


            const res =
              await fetch(
                registerUrl,
                {
                  method:
                    "POST",

                  headers: {
                    "Content-Type":
                      "application/json",
                  },

                  body:
                    JSON.stringify({
                      authUid,
                      email,
                      name,

                      plan:
                        selectedPlan,

                      source:
                        registrationSource,
                    }),
                }
              );


            let payload =
              null;


            try {
              payload =
                await res.json();
            }
            catch {
              payload =
                null;
            }


            // ================================================
            // REGISTRATION FAILED
            // ================================================

            if (
              !res.ok
            ) {
              await supabase.auth
                .signOut();

              cleanup();


              if (
                res.status ===
                409
              ) {
                const returnTarget =
                  localStorage.getItem(
                    "comment_register_return"
                  ) ||
                  localStorage.getItem(
                    "redirect_after_login"
                  ) ||
                  qs.get(
                    "return"
                  );


                const loginUrl =
                  returnTarget
                    ? `/login?err=already_registered&return=${encodeURIComponent(
                        returnTarget
                      )}`
                    : "/login?err=already_registered";


                navigate(
                  loginUrl,
                  {
                    replace:
                      true,
                  }
                );

                return;
              }


              cleanupFreeToolRegistrationState();


              navigate(
                "/?err=register_failed#pricing",
                {
                  replace:
                    true,
                }
              );

              return;
            }


            // ================================================
            // DETERMINE FINAL PLAN
            // ================================================

            const finalPlan =
              normalizePlan(
                payload?.plan
              ) ||
              selectedPlan ||
              (
                payload?.is_premium
                  ? "deep_audit"
                  : "snapshot"
              );


            localStorage.setItem(
              "selectedPlan",
              finalPlan
            );


            sessionStorage.setItem(
              "user_plan",
              finalPlan
            );


            localStorage.setItem(
              `user_plan_${authUid}`,
              finalPlan
            );


            sessionStorage.setItem(
              "db_verified",
              "1"
            );


            // ================================================
            // PHASE 6C
            // Successful confirmed/OAuth blog signup
            // ================================================

            trackBlogSignupIfNeeded(
              authUid,
              finalPlan
            );


            // ----------------------------------------------
            // Persist source for dashboard/session context
            // ----------------------------------------------

            localStorage.setItem(
              "registrationSource",
              registrationSource
            );


            // ================================================
            // BLOG COMMENT RETURN
            // ================================================

            if (
              commentReturn
            ) {
              localStorage.removeItem(
                "comment_register_return"
              );


              localStorage.removeItem(
                "redirect_after_login"
              );


              cleanup();


              navigate(
                commentReturn,
                {
                  replace:
                    true,
                }
              );

              return;
            }


            // ================================================
            // NORMAL RETURN TARGET
            // ================================================

            const returnFromQuery =
              safeDecode(
                qs.get(
                  "return"
                )
              );


            const returnFromStorage =
              localStorage.getItem(
                "redirect_after_login"
              );


            const returnTarget =
              returnFromQuery ||
              returnFromStorage;


            localStorage.removeItem(
              "redirect_after_login"
            );


            cleanup();


            // ================================================
            // EXPLICIT RETURN
            // ================================================

            if (
              returnTarget
            ) {
              navigate(
                returnTarget,
                {
                  replace:
                    true,
                }
              );

              return;
            }


            // ================================================
            // FREE TOOLS -> DASHBOARD
            // ================================================

            if (
              finalPlan ===
              "free_calculator"
            ) {
              navigate(
                "/dashboard",
                {
                  replace:
                    true,
                }
              );

              return;
            }


            // ================================================
            // CAREER AUDIT -> INSTRUCTIONS
            // ================================================

            cleanupFreeToolRegistrationState();


            navigate(
              "/instructions",
              {
                replace:
                  true,
              }
            );

            return;
          }
          catch (
            error
          ) {
            console.error(
              "OAuth registration failed:",
              error
            );


            cleanup();


            navigate(
              "/login?err=server_down",
              {
                replace:
                  true,
              }
            );

            return;
          }
        }


        // ====================================================
        // LOGIN FLOW
        // ====================================================

        let status;


        try {
          const statusUrl =
            `${API_BASE_URL}/api/users/oauth-status`;


          console.log(
            "[AuthCallback] OAuth status API:",
            statusUrl
          );


          const res =
            await fetch(
              statusUrl,
              {
                method:
                  "POST",

                headers: {
                  "Content-Type":
                    "application/json",
                },

                body:
                  JSON.stringify({
                    authUid,
                    email,
                  }),
              }
            );


          try {
            status =
              await res.json();
          }
          catch {
            status =
              null;
          }


          if (
            !res.ok
          ) {
            throw new Error(
              status?.message ||
              "Unable to verify account."
            );
          }
        }
        catch (
          error
        ) {
          console.error(
            "OAuth status check failed:",
            error
          );


          cleanup();


          navigate(
            "/login?err=server_down",
            {
              replace:
                true,
            }
          );

          return;
        }


        const exists =
          !!status?.exists;


        const registrationComplete =
          !!status
            ?.registration_complete;


        // ====================================================
        // USER EXISTS IN SUPABASE AUTH BUT NOT APPLICATION DB
        // ====================================================

        if (
          !exists ||
          !registrationComplete
        ) {
          await supabase.auth
            .signOut();


          sessionStorage.removeItem(
            "db_verified"
          );


          cleanup();


          navigate(
            "/?err=not_registered#pricing",
            {
              replace:
                true,
            }
          );

          return;
        }


        // ====================================================
        // RESTORE PLAN
        // ====================================================

        const dbPlan =
          normalizePlan(
            status?.plan
          ) ||
          (
            status?.is_premium
              ? "deep_audit"
              : "snapshot"
          );


        const dbSource =
          normalizeSource(
            status?.source
          );


        localStorage.setItem(
          "selectedPlan",
          dbPlan
        );


        sessionStorage.setItem(
          "user_plan",
          dbPlan
        );


        localStorage.setItem(
          `user_plan_${authUid}`,
          dbPlan
        );


        sessionStorage.setItem(
          "db_verified",
          "1"
        );


        // ================================================
        // PHASE 6C
        // Covers email-confirmed blog registrations that
        // return through the authenticated callback/login path.
        // trackAttributedEventOnce prevents duplicate counting.
        // ================================================

        trackBlogSignupIfNeeded(
          authUid,
          dbPlan
        );


        if (
          dbSource
        ) {
          localStorage.setItem(
            "registrationSource",
            dbSource
          );
        }


        // ====================================================
        // BLOG RETURN
        // ====================================================

        if (
          commentReturn
        ) {
          localStorage.removeItem(
            "comment_register_return"
          );


          localStorage.removeItem(
            "redirect_after_login"
          );


          cleanup();


          navigate(
            commentReturn,
            {
              replace:
                true,
            }
          );

          return;
        }


        // ====================================================
        // DETERMINE LOGIN REDIRECT
        // ====================================================

        const targetFromQuery =
          safeDecode(
            qs.get(
              "return"
            )
          );


        const targetFromStorage =
          localStorage.getItem(
            "redirect_after_login"
          );


        const target =
          targetFromQuery ||
          targetFromStorage ||
          "/dashboard";


        localStorage.removeItem(
          "redirect_after_login"
        );


        cleanup();


        navigate(
          target,
          {
            replace:
              true,
          }
        );
      };


    run();
  }, [
    navigate,
  ]);


  // ==========================================================
  // PROCESSING UI
  // ==========================================================

  return (
    <div
      style={{
        minHeight:
          "100vh",

        background:
          "#0f0a24",

        color:
          "white",

        display:
          "flex",

        alignItems:
          "center",

        justifyContent:
          "center",

        padding:
          20,
      }}
    >
      Processing…
    </div>
  );
}